// src/encounter/lib/lab-vendor.ts
// Pure helpers for the lab order's performing vendor (PROD-2653).
//
// DEFAULT RULE for a NEW lab order, in order:
//   1. the picked catalogue item names a vendor (`labVendorId`) that is one of the clinic's active
//      vendors → that vendor. The catalogue v2 DTOs carry labVendorId; today's picker endpoints
//      (LabOrderCodesList / LabOrderValueSetListByCode) do not, so this step is inert until the
//      picker reads the v2 catalogue. It is read here so nothing else has to change when it does.
//   2. the clinic has exactly ONE active vendor → that vendor.
//   3. otherwise nothing: "Not set". Allowed; it never blocks the save.
import type { CodeSearchResult } from '@scripsteam/scrips-signal-ds';
import type { OrderLabs } from '@/types/assessment-api';
import type { LabVendor } from '../services/use-lab-vendors';
import type { LabVendorRef } from './order-payload';

export function pickDefaultVendor(
  vendors: readonly LabVendor[] | undefined,
  catalogueVendorId?: string | null,
): LabVendor | undefined {
  const list = vendors ?? [];
  if (catalogueVendorId) {
    const hit = list.find((v) => v.id.toLowerCase() === catalogueVendorId.toLowerCase());
    if (hit) return hit;
  }
  return list.length === 1 ? list[0] : undefined;
}

/** The catalogue item's vendor id, if the row it came from carried one (catalogue v2 shape). */
export function catalogueVendorIdOf(item: CodeSearchResult | undefined): string | null {
  if (!item) return null;
  const raw = (item as CodeSearchResult & { labVendorId?: unknown }).labVendorId;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

/** A master-data vendor → the payload ref the save mapper reads. */
export function toLabVendorRef(v: LabVendor): LabVendorRef {
  return { id: v.id, name: v.name, address: v.address ?? null, phone: v.phone ?? null };
}

/** Payload ref → wire `orderLabs` (PROD-2653 contract). No vendor → null, as sent before. */
export function toOrderLabs(vendor: LabVendorRef | undefined): OrderLabs | null {
  if (!vendor) return null;
  return {
    ...(vendor.orderLabsId ? { id: vendor.orderLabsId } : {}),
    ...(vendor.serviceOrderId ? { serviceOrderId: vendor.serviceOrderId } : {}),
    labId: vendor.id,
    labName: vendor.name,
    labAddress: vendor.address ?? null,
    labContactNo: vendor.phone ?? null,
    isActive: true,
    isDeleted: false,
  };
}

/** Wire `orderLabs` → payload ref. Only a row that names a vendor (labId) counts. */
export function fromOrderLabs(row: OrderLabs | null | undefined): LabVendorRef | undefined {
  const id = row?.labId?.trim();
  if (!row || !id || row.isDeleted) return undefined;
  return {
    id,
    name: row.labName?.trim() || '',
    address: row.labAddress ?? null,
    phone: row.labContactNo ?? null,
    orderLabsId: row.id ?? null,
    serviceOrderId: row.serviceOrderId ?? null,
  };
}
